import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Search, Menu, X, Home } from 'lucide-react';
import { Button } from './ui/button';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/categories/header`);
      setCategories(response.data.categories);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const navItems = [
    { name: '50 Years', href: '/50-years' },
    { name: 'Photo Archives', href: '/photo-archives' },
    { name: 'Publications', href: '/publications' },
    { name: 'Citizen Reporter', href: '/citizen-reporter' },
    { name: 'Subscribe', href: '/subscribe' },
  ];

  return (
    <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
      {/* Top Header with Container */}
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900" style={{ fontFamily: 'serif' }}>
              ত্রিপুরা দর্পণ
            </h1>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8">
            {navItems.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className="text-gray-700 hover:text-red-600 font-medium transition-colors duration-200"
              >
                {item.name}
              </Link>
            ))}
            <Link
              to="/epaper"
              className="flex items-center text-gray-700 hover:text-red-600 font-medium transition-colors duration-200"
            >
              <span className="mr-1">📰</span>
              E-Paper
            </Link>
          </nav>

          {/* Right side buttons */}
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setIsSearchOpen(!isSearchOpen)}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors duration-200"
              aria-label="Search"
            >
              <Search className="w-5 h-5 text-gray-700" />
            </button>
            <Button className="hidden md:block bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded">
              বাংলা
            </Button>
            <Button className="hidden md:block bg-gray-900 hover:bg-gray-800 text-white px-4 py-2 rounded">
              English
            </Button>
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="lg:hidden p-2 hover:bg-gray-100 rounded-full transition-colors duration-200"
              aria-label="Menu"
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Full-Width Category Navigation Bar */}
      <div className="hidden lg:block border-t border-gray-200 bg-gray-50">
        <div className="w-full px-8">
          <div className="flex items-center justify-evenly py-3">
            {/* Home Link First */}
            <Link
              to="/"
              className="text-sm font-medium text-gray-700 hover:text-red-600 whitespace-nowrap transition-colors duration-200 px-4 flex items-center gap-1"
            >
              <Home className="w-4 h-4" />
              হোম
            </Link>
            {/* Category Links */}
            {categories.map((category) => (
              <Link
                key={category.bengali}
                to={`/category/${encodeURIComponent(category.bengali)}`}
                className="text-sm font-medium text-gray-700 hover:text-red-600 whitespace-nowrap transition-colors duration-200 px-4"
              >
                {category.bengali}
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Search Bar - Keep in container */}
      <div className="container mx-auto px-4">

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="lg:hidden py-4 border-t border-gray-200">
            <nav className="flex flex-col space-y-3">
              {/* Main Navigation */}
              {navItems.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className="text-gray-700 hover:text-red-600 font-medium py-2 transition-colors duration-200"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {item.name}
                </Link>
              ))}
              <Link
                to="/epaper"
                className="text-gray-700 hover:text-red-600 font-medium py-2 transition-colors duration-200"
                onClick={() => setIsMenuOpen(false)}
              >
                📰 E-Paper
              </Link>
              
              {/* Category Navigation in Mobile */}
              <div className="pt-3 mt-3 border-t border-gray-200">
                <p className="text-xs font-semibold text-gray-500 uppercase mb-2">বিভাগ (Categories)</p>
                {/* Home Link First in Mobile */}
                <Link
                  to="/"
                  className="block text-gray-700 hover:text-red-600 font-medium py-2 transition-colors duration-200 flex items-center gap-2"
                  onClick={() => setIsMenuOpen(false)}
                >
                  <Home className="w-4 h-4" />
                  হোম
                </Link>
                {categories.map((category) => (
                  <Link
                    key={category.bengali}
                    to={`/category/${encodeURIComponent(category.bengali)}`}
                    className="block text-gray-700 hover:text-red-600 font-medium py-2 transition-colors duration-200"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {category.bengali}
                  </Link>
                ))}
              </div>
            </nav>
          </div>
        )}

        {/* Search Bar */}
        {isSearchOpen && (
          <div className="py-4 border-t border-gray-200">
            <input
              type="text"
              placeholder="Search news..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-600 focus:border-transparent"
              autoFocus
            />
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;